import React, { useRef, useEffect, useState, Suspense, useMemo } from 'react';
import { Canvas, useFrame, useThree, dispose } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, useGLTF } from '@react-three/drei';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import * as THREE from 'three';
import { animated as a, useTransition as useTransition3, useSpring, config } from '@react-spring/three';
import { animated as aWeb, useTransition as useTransitionWeb } from '@react-spring/web';
import './GLBViewer.css';

// FIX 1: regex era /^http?/ (matcheaba "htt://"), debe ser /^https?/
//         Codifica cada segmento para manejar espacios y em-dash en nombres de periodo
const resolveModelUrl = (url: string): string => {
  if (!url) return url;
  if (/^https?:\/\//.test(url)) return url;

  const safeDecode = (s: string) => { try { return decodeURIComponent(s); } catch { return s; } };
  const encoded = url.split('/').map(seg => seg ? encodeURIComponent(safeDecode(seg)) : seg).join('/');

  if (encoded.startsWith('/paca/')) return encoded;
  if (encoded.startsWith('/')) return `/paca${encoded}`;
  return `/paca/${encoded}`;
};

export type ParsedPoint = Record<string, any>;

interface GLBViewerProps {
  modelUrl: string;
  originalPoints?: ParsedPoint[];
  rotatedPoints?: ParsedPoint[] | null;
  selectedPeriod?: string | null;
  selectedPeriodLabel?: string | null; 
  exportEnabled?: boolean;
}


const SCALE = 0.6;

function latLngToXYZ(lat: number, lng: number, radius = 1): [number, number, number] {
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (-lng * Math.PI) / 180;
  const x = radius * Math.cos(latRad) * Math.cos(lonRad);
  const y = radius * Math.sin(latRad);
  const z = radius * Math.cos(latRad) * Math.sin(lonRad);
  return [x, y, z];
}


function parseLatLngFromPoint(p: any, isRotated: boolean = false): { lat: number; lng: number } {
  let rawLat, rawLng;
  
  if (isRotated) {
    // ← rotatedPoints: usar p_lat/p_lng (paleocoords)
    rawLat = p.p_lat ?? p.pLat ?? '';
    rawLng = p.p_lng ?? p.pLng ?? '';
  } else {
    // originalPoints: usar lat/lng (modernas)
    rawLat = p.lat ?? p.Lat ?? p.latitude ?? p.Latitude ?? '';
    rawLng = p.lng ?? p.Lng ?? p.longitude ?? p.Longitude ?? '';
  }
  
  const lat = Number(String(rawLat).trim().replace(',', '.'));
  const lng = Number(String(rawLng).trim().replace(',', '.'));
  
  // swap si lat>180 (fallback)
  if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) > 180 && Math.abs(lng) <= 90) {
    return { lat: lng, lng: lat };
  }
  
  return { 
    lat: Number.isFinite(lat) ? lat : NaN, 
    lng: Number.isFinite(lng) ? lng : NaN 
  };
}


function Model({ url, isRotated = false }: { url: string; isRotated?: boolean }) {
  const resolved = resolveModelUrl(url);
  const { scene } = useGLTF(resolved);

  useEffect(() => {
    return () => {
      dispose(scene);
      useGLTF.clear(resolved);
    };
  }, [resolved, scene]);

  return (
    <primitive
      object={scene}
      rotation={isRotated ? [0, 0, Math.PI] : [0, 0, 0]}
    />
  );
}


// FIX 3: ErrorBoundary para que un GLB corrupto no crashee todo el Canvas
class ModelErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidUpdate(prevProps: { children: React.ReactNode }) {
    // Resetear el error cuando cambia el modelo (nuevo url)
    if (prevProps.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function PulsingHalo({
  position,
  scale = 1,
  visible = true,
  color = 'red',
}: {
  position: [number, number, number];
  scale?: number;
  visible?: boolean;
  color?: string;
}) {
  const meshRef = useRef<THREE.Mesh>(null);

  const { opacity } = useSpring({
    opacity: visible ? 0.3 : 0,
    config: { tension: 80, friction: 20 },
  });

  useFrame(({ clock }) => {
    const baseScale = 0.03 + 0.01 * Math.sin(clock.getElapsedTime() * 4);
    if (meshRef.current) meshRef.current.scale.set(baseScale * scale, baseScale * scale, baseScale * scale);
  });

  return (
    <a.mesh position={position} ref={meshRef}>
      <sphereGeometry args={[1, 16, 16]} />
      <a.meshBasicMaterial color={color} transparent opacity={opacity} />
    </a.mesh>
  );
}

function AnimatedPoint({
  lat,
  lng,
  visible = true,
  color = 'red',
  size = 0.015 * SCALE,
  showHalo = true,
}: {
  lat: number;
  lng: number;
  visible?: boolean;
  color?: string;
  size?: number;
  showHalo?: boolean;
}) {
  const position = latLngToXYZ(lat, lng, SCALE * 1.01); 

  const { scale, opacity } = useSpring({
    scale: visible ? 1 : 0.2,
    opacity: visible ? 1 : 0,
    config: { tension: 150, friction: 22 },
  });

  return (
    <>
      <a.mesh position={position} scale={scale.to((s) => [s * size, s * size, s * size])}>
        <sphereGeometry args={[1, 64, 64]} />
        <a.meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={1.5}
          transparent
          opacity={opacity}
        />
      </a.mesh>
      {showHalo && <PulsingHalo position={position} scale={SCALE} visible={visible} color={color} />}
    </>
  );
}

function SceneExporter({
  onExport,
  selectedPeriod,
}: {
  onExport: (success: boolean, message: string) => void;
  selectedPeriod?: string | null;
}) {
  const { scene } = useThree();
  const hasExported = useRef(false);

  useEffect(() => {
    if (hasExported.current) return;
    hasExported.current = true;

    const exporter = new GLTFExporter();
    const fileName = `${selectedPeriod || 'map'}_${new Date().toISOString().slice(0, 10)}.glb`;

    exporter.parse(
      scene,
      (result) => {
        if (result instanceof ArrayBuffer) {
          const blob = new Blob([result], { type: 'application/octet-stream' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 100);
          onExport(true, '3D model exported successfully');
        } else {
          onExport(false, 'Export: unexpected result type');
        }
      },
      () => onExport(false, 'Error exporting the 3D model'),
      { binary: true, maxTextureSize: 4096, includeCustomExtensions: false }
    );
  }, [scene, onExport, selectedPeriod]);

  return null;
}

const GLBViewer: React.FC<GLBViewerProps> = ({
  modelUrl,
  originalPoints = [],
  rotatedPoints = null,
  selectedPeriod = null,
  selectedPeriodLabel = null, 
  exportEnabled = true,
}) => {

  const [activeUrl,          setActiveUrl]          = useState(modelUrl);
  const [previousPoints,     setPreviousPoints]     = useState<ParsedPoint[]>([]);
  const [currentPoints,      setCurrentPoints]      = useState<ParsedPoint[]>([]);
  const [showCurrentPoints,  setShowCurrentPoints]  = useState(true);

  const [autoRotateEnabled,    setAutoRotateEnabled]    = useState(true);
  const [rotationSpeed,        setRotationSpeed]        = useState(0.5);
  const [zoomEnabled,          setZoomEnabled]          = useState(false);
  const [showHalo,             setShowHalo]             = useState(true);
  const [pointColor,           setPointColor]           = useState('#ff0000');
  const [pointSize,            setPointSize]            = useState(0.015 * SCALE);
  const [ambientLightIntensity,setAmbientLightIntensity]= useState(0.6);
  const [settingsOpen,         setSettingsOpen]         = useState(false);
  const [exporting,            setExporting]            = useState(false);
  const [exportMessage,        setExportMessage]        = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const settingsRef   = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // FIX 4: ref para comparar currentPoints sin incluirlo en el dep array del efecto principal
  //         Evita el bucle useEffect → setState → useEffect con JSON.stringify en datasets grandes
  const currentPointsRef = useRef<ParsedPoint[]>([]);
  useEffect(() => { currentPointsRef.current = currentPoints; }, [currentPoints]);

  // Preload del modelo actual (resolveModelUrl ya codifica la URL)
  useEffect(() => {
    useGLTF.preload(resolveModelUrl(modelUrl));
  }, [modelUrl]);

  // Puntos actuales parseados (memoizados)
  const parsedCurrentPoints = useMemo(() => {
    const isRotatedData = rotatedPoints && rotatedPoints.length > 0;
    return currentPoints.map((p) => {
      const { lat, lng } = parseLatLngFromPoint(p, isRotatedData);  // ← flag siempre
      return isNaN(lat) || isNaN(lng) ? null : { ...p, __lat: lat, __lng: lng };
    }).filter(Boolean) as Array<ParsedPoint & { __lat: number; __lng: number }>;
  }, [currentPoints, rotatedPoints]);  // deps correctos
  
  

  const filterPoints = useMemo(() => {
    return (pts: ParsedPoint[]) => {
      if (selectedPeriod && pts.every((p) => p.period)) {
        return pts.filter((p) => p.period === selectedPeriod);
      }
      return pts;
    };
  }, [selectedPeriod]);

  // Gestionar cambio de modelo y puntos — currentPoints via ref para evitar loop
  useEffect(() => {
    const newPoints = rotatedPoints && rotatedPoints.length > 0 ? rotatedPoints : originalPoints;

    if (modelUrl !== activeUrl) {
      setPreviousPoints(currentPointsRef.current);
      setShowCurrentPoints(false);

      const timeoutId = setTimeout(() => {
        setActiveUrl(modelUrl);
        if (newPoints.length === 0) {
          setCurrentPoints([]);
          setPreviousPoints([]);
        } else {
          setCurrentPoints(filterPoints(newPoints));
        }
      }, 400);

      return () => clearTimeout(timeoutId);
    } else {
      if (newPoints.length === 0) {
        if (currentPointsRef.current.length > 0) {
          setCurrentPoints([]);
          setPreviousPoints([]);
        }
      } else {
        const filtered = filterPoints(newPoints);
        // Comparación superficial por longitud + JSON para evitar setState innecesario
        const prev = currentPointsRef.current;
        if (prev.length !== filtered.length || JSON.stringify(prev) !== JSON.stringify(filtered)) {
          setCurrentPoints(filtered);
        }
      }
    }
  }, [modelUrl, originalPoints, rotatedPoints, activeUrl, filterPoints]);

  // Mostrar puntos después de que cargue el nuevo modelo
  useEffect(() => {
    if (modelUrl === activeUrl) {
      const timer = setTimeout(() => {
        setShowCurrentPoints(true);
        if (currentPointsRef.current.length === 0) setPreviousPoints([]);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [modelUrl, activeUrl]);

  // Transición 3D del modelo
  const modelTransition = useTransition3(activeUrl, {
    from:   { opacity: 0, scale: 0.4, position: [0, -0.2, 0] as [number, number, number] },
    enter:  { opacity: 1, scale: 1,   position: [0,  0.0, 0] as [number, number, number] },
    leave:  { opacity: 0, scale: 0.4, position: [0,  0.2, 0] as [number, number, number] },
    config: config.slow,
  });

  const handleExport3DModel = () => {
    setExporting(true);
    setExportMessage({ type: 'success', text: 'Preparing export...' });
  };

  const handleExportComplete = (success: boolean, message: string) => {
    setExportMessage({ type: success ? 'success' : 'error', text: message });
    setExporting(false);
    setTimeout(() => setExportMessage(null), 3000);
  };

  const handleColorClick = () => colorInputRef.current?.click();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setSettingsOpen(false);
      }
    }
    if (settingsOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [settingsOpen]);

  const settingsTransition = useTransitionWeb(settingsOpen, {
    from:   { opacity: 0, transform: 'translateY(20px) scale(0.95)' },
    enter:  { opacity: 1, transform: 'translateY(0px) scale(1)' },
    leave:  { opacity: 0, transform: 'translateY(20px) scale(0.95)' },
    config: { tension: 300, friction: 25 },
  });

  return (
    <div className="glb-viewer-container relative" style={{ width: '100%', height: '100%' }}>
      <Canvas
        dpr={[1, 2]}
        shadows
        gl={{
          antialias: true,
          alpha: true,
          toneMapping: THREE.ACESFilmicToneMapping,
          outputColorSpace: THREE.SRGBColorSpace,
        }}
        style={{ background: 'transparent', width: '100%', height: '100%' }}
        camera={{ position: [0, 0, 3], fov: 45 }}
      >
        <Suspense fallback={null}>
          <PerspectiveCamera makeDefault position={[0, 0, 2]} fov={45} />
          <ambientLight intensity={ambientLightIntensity} />
          <directionalLight position={[5, 5, 5]} intensity={1} castShadow />
          <OrbitControls
            enableZoom={zoomEnabled}
            autoRotate={autoRotateEnabled}
            autoRotateSpeed={rotationSpeed}
            enablePan={false}
          />

          {/* FIX 3: ModelErrorBoundary evita que un GLB corrupto crashee el Canvas entero */}
          {modelTransition((style, url) => (
            <a.group
              key={url}
              scale={style.scale.to((s) => [s * SCALE, s * SCALE, s * SCALE])}
              position={style.position.to((x, y, z) => [x, y, z])}
            >
              <ModelErrorBoundary>
                <Model url={url} isRotated={!!(rotatedPoints && rotatedPoints.length > 0)} />
              </ModelErrorBoundary>
            </a.group>
          ))}

          {previousPoints.map((p, idx) => {
            const { lat, lng } = parseLatLngFromPoint(p);
            if (isNaN(lat) || isNaN(lng)) return null;
            return (
              <AnimatedPoint
                key={`prev-${idx}`}
                lat={lat}
                lng={lng}
                visible={false}
                color={pointColor}
                size={pointSize}
                showHalo={showHalo}
              />
            );
          })}

          {parsedCurrentPoints.map((p, idx) => (
            <AnimatedPoint
              key={`curr-${idx}`}
              lat={p.__lat}
              lng={p.__lng}
              visible={showCurrentPoints}
              color={pointColor}
              size={pointSize}
              showHalo={showHalo}
            />
          ))}

          {exportEnabled && exporting && (
            <SceneExporter onExport={handleExportComplete} selectedPeriod={selectedPeriod} />
          )}
        </Suspense>
      </Canvas>

      {exportEnabled && (
        <div className="absolute bottom-4 left-4 z-30">
          <button
            onClick={handleExport3DModel}
            aria-label="Export 3D model map"
            title="Download 3D model map (.glb)"
            className="export-button"
            disabled={exporting}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
              viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            <span className="text-sm font-medium">{exporting ? 'Exporting...' : 'Export 3D'}</span>
          </button>
        </div>
      )}

      {exportMessage && (
        <div className={`export-message ${exportMessage.type}`}>{exportMessage.text}</div>
      )}

      <div className="map-caption">
        {selectedPeriodLabel && (
          <p>3D Map of the Earth during the {selectedPeriodLabel}</p>
        )}
      </div>

      {/* Settings */}
      <div className="absolute bottom-4 right-4 z-20">
        <div className="relative inline-block">
          <button
            onClick={() => setSettingsOpen((o) => !o)}
            aria-label="Toggle settings"
            className="settings-button"
          >
            <svg xmlns="http://www.w3.org/2000/svg"
              className={`settings-icon ${settingsOpen ? 'open' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.18a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>

          {settingsTransition((style, item) =>
            item ? (
              <aWeb.div ref={settingsRef} style={style} className="settings-panel">
                <div className="settings-header"><h3>Settings</h3></div>

                <div className="settings-section">
                  <div className="section-title">Viewer</div>
                  <div className="section-spacer"></div>
                  <div className="setting-item">
                    <label htmlFor="autoRotate">Auto Rotate</label>
                    <label className="toggle-switch">
                      <input id="autoRotate" type="checkbox" checked={autoRotateEnabled}
                        onChange={(e) => setAutoRotateEnabled(e.target.checked)} />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <label htmlFor="zoom">Zoom</label>
                    <label className="toggle-switch">
                      <input id="zoom" type="checkbox" checked={zoomEnabled}
                        onChange={(e) => setZoomEnabled(e.target.checked)} />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  {autoRotateEnabled && (
                    <div className="setting-item slider-item">
                      <label htmlFor="rotationSpeed">Speed</label>
                      <div className="slider-container">
                        <input id="rotationSpeed" type="range" min="0" max="2" step="0.1"
                          value={rotationSpeed}
                          onChange={(e) => setRotationSpeed(parseFloat(e.target.value))}
                          className="range-slider" />
                        <span className="slider-value">{rotationSpeed.toFixed(1)}</span>
                      </div>
                    </div>
                  )}

                  <div className="setting-item slider-item">
                    <label htmlFor="ambientLight">Light</label>
                    <div className="slider-container">
                      <input id="ambientLight" type="range" min="0" max="1" step="0.05"
                        value={ambientLightIntensity}
                        onChange={(e) => setAmbientLightIntensity(parseFloat(e.target.value))}
                        className="range-slider" />
                      <span className="slider-value">{ambientLightIntensity.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="settings-section">
                  <div className="section-title">Points</div>
                  <div className="section-spacer"></div>
                  <div className="setting-item">
                    <label htmlFor="pointColor">Color</label>
                    <div className="color-picker-wrapper">
                      <input ref={colorInputRef} type="color" id="pointColor"
                        value={pointColor} onChange={(e) => setPointColor(e.target.value)}
                        className="color-input" />
                      <div className="color-preview"
                        style={{ backgroundColor: pointColor }} onClick={handleColorClick} />
                    </div>
                  </div>

                  <div className="setting-item slider-item">
                    <label htmlFor="pointSize">Size</label>
                    <div className="slider-container">
                      <input id="pointSize" type="range" min="0.005" max="0.05" step="0.001"
                        value={pointSize}
                        onChange={(e) => setPointSize(parseFloat(e.target.value))}
                        className="range-slider" />
                      <span className="slider-value">{(pointSize * 100).toFixed(1)}</span>
                    </div>
                  </div>

                  <div className="setting-item">
                    <label htmlFor="showHalo">Halo</label>
                    <label className="toggle-switch">
                      <input id="showHalo" type="checkbox" checked={showHalo}
                        onChange={(e) => setShowHalo(e.target.checked)} />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </aWeb.div>
            ) : null
          )}
        </div>
      </div>
    </div>
  );
};

export default GLBViewer;
